/**
 * M03 Playbook 列表 · S2 Day 7b
 *
 * 数据源：usePlaybookList → GET /admin/v1/playbook/list
 * 显示：N PB 行 · intent_bindings + branch + allowed_skills_count + step_budget + has_pre_fetch
 *
 * onSelect: 点 PB 行 → 切到 "PB 编辑" tab + 载入对应 PB（由父组件处理）
 */
import type { PlaybookListItem, PlaybookMatchTags } from "@/lib/api/playbook";
import { usePlaybookList } from "@/lib/api/playbook";
import { intentLabel, matchTagDimLabel, matchTagValueLabel } from "@/lib/product-labels";

// match_tags 五维展示顺序（T1 ontology）
const MATCH_TAG_DIMS: (keyof PlaybookMatchTags)[] = [
	"intent_tags",
	"sale_stage",
	"order_status",
	"product_tags",
	"dialogue_state",
];

/**
 * 命中触发条件单元格：把 PB 的 match_tags 五维渲染成"维度：中文标签"chips。
 * 这是该 PB 被标签召回选中的实际条件（运行逻辑的配置面），让运营直接读懂"什么情况用这个方案"。
 * match_tags 为空 = 该 PB 尚未启用标签召回，仍走 RoutingTable 快路径。
 */
function MatchTagsCell({ matchTags }: { matchTags?: PlaybookMatchTags | null }) {
	const rows = matchTags
		? MATCH_TAG_DIMS.map((dim) => {
				const values = matchTags[dim] ?? [];
				if (values.length === 0) return null;
				return (
					<div key={dim} className="flex flex-wrap items-baseline gap-1">
						<span className="text-xs text-text-muted">{matchTagDimLabel(dim)}：</span>
						{values.map((v) => (
							<span
								key={v}
								className="rounded bg-brand-info/10 px-1.5 py-0.5 text-xs text-brand-info"
							>
								{matchTagValueLabel(dim, v)}
							</span>
						))}
					</div>
				);
			}).filter(Boolean)
		: [];
	if (rows.length === 0) {
		return <span className="text-xs text-text-muted">未配置（走快路径）</span>;
	}
	return <div className="space-y-1">{rows}</div>;
}

const PLAYBOOK_ABSORPTION: Record<
	string,
	{
		capability: string;
		source: string;
		boundary: string;
	}
> = {
	PB01_order_query: {
		capability: "订单查询、物流跟踪",
		source: "search-order / logistics-define",
		boundary: "多订单先选择，事实来自订单和履约证据。",
	},
	PB02_refund: {
		capability: "退款办理、状态确认",
		source: "skill-refund / 订单售后",
		boundary: "先核订单和政策，写操作必须确认和保护。",
	},
	PB03_return: {
		capability: "退货办理、寄回流程",
		source: "skill-refund / 售后退货分支",
		boundary: "退货和退款分开说明，退货创建不等于立即退款。",
	},
	PB04_cancel: {
		capability: "取消订单、退款联动",
		source: "订单售后 / 写操作保护",
		boundary: "未发货才可取消；已发货转退货或人工。",
	},
	PB05_address_change: {
		capability: "地址修改、确认执行",
		source: "skill-deliver-address",
		boundary: "必须核对完整地址，执行前进入 dry-run / 保护链。",
	},
	PB06_pre_sale: {
		capability: "导购、推荐、对比、找相似",
		source: "general_shop_guide / product-compare / find_similar",
		boundary: "仍是同一售前导购域，不拆新方案；对比必须基于商品证据。",
	},
	PB07_discount: {
		capability: "省钱 / 优惠方案",
		source: "save-money-plan",
		boundary: "只回答可验证优惠、限制和叠加规则，不编造活动。",
	},
};

interface Props {
	onSelect: (pbId: string) => void;
	selectedPbId: string | null;
}

export function PlaybookList({ onSelect, selectedPbId }: Props) {
	const { data, isLoading, isError, error } = usePlaybookList();

	if (isLoading) {
		return (
			<div data-testid="pb-list-loading" className="p-4 text-sm text-text-secondary">
				加载 Playbook 列表中...
			</div>
		);
	}
	if (isError) {
		return (
			<div data-testid="pb-list-error" className="p-4 text-sm text-brand-danger">
				加载失败：{error instanceof Error ? error.message : String(error)}
			</div>
		);
	}

	const items: PlaybookListItem[] = data?.items ?? [];

	return (
		<div className="space-y-3" data-testid="pb-list">
			<div className="flex items-baseline justify-between">
				<div className="text-sm text-text-secondary">共 {data?.total ?? 0} 个处理方案</div>
				<div className="text-xs text-text-muted">淘宝对标落在现有方案内，不新增一排同名方案</div>
			</div>
			<div className="overflow-x-auto rounded-md border border-surface-border">
				<table className="w-full border-collapse text-sm">
					<thead className="bg-surface-subtle">
						<tr className="text-left">
							<th className="px-3 py-2 font-medium">处理方案</th>
							<th className="px-3 py-2 font-medium">吸收落点</th>
							<th className="px-3 py-2 font-medium">适用分类</th>
							<th className="px-3 py-2 font-medium">命中触发条件</th>
							<th className="px-3 py-2 font-medium">方案分支</th>
							<th className="px-3 py-2 font-medium">可用工具数</th>
							<th className="px-3 py-2 font-medium">步骤上限</th>
							<th className="px-3 py-2 font-medium">预取事实</th>
						</tr>
					</thead>
					<tbody>
						{items.map((pb) => {
							const selected = selectedPbId === pb.playbook_id;
							const absorption = PLAYBOOK_ABSORPTION[pb.playbook_id];
							return (
								<tr
									key={pb.playbook_id}
									data-testid={`pb-row-${pb.playbook_id}`}
									className={`border-t border-surface-border ${
										selected ? "bg-brand-accent/5" : ""
									}`}
								>
									<td className="px-3 py-2">
										<button
											type="button"
											onClick={() => onSelect(pb.playbook_id)}
											data-testid={`pb-row-link-${pb.playbook_id}`}
											className="text-left hover:underline focus:outline-none focus:ring-2 focus:ring-brand-accent"
										>
											<div className="font-mono text-xs">{pb.playbook_id}</div>
											<div className="mt-0.5 text-text-primary">{pb.name}</div>
											<div className="text-xs text-text-muted">v{pb.version}</div>
										</button>
									</td>
									<td className="max-w-sm px-3 py-2">
										{absorption ? (
											<div className="space-y-1">
												<div className="text-text-primary">{absorption.capability}</div>
												<div className="text-xs text-text-muted">淘宝对标：{absorption.source}</div>
												<div className="text-xs text-text-secondary">{absorption.boundary}</div>
											</div>
										) : (
											<span className="text-xs text-text-muted">按现有方案维护</span>
										)}
									</td>
									<td className="px-3 py-2">
										<div className="flex flex-wrap gap-1">
											{pb.intent_bindings.map((it) => (
												<span
													key={it}
													className="rounded bg-brand-info/10 px-1.5 py-0.5 text-xs text-brand-info"
												>
													{intentLabel(it)}
												</span>
											))}
											{pb.intent_bindings.length === 0 ? (
												<span className="text-xs text-text-muted">—</span>
											) : null}
										</div>
									</td>
									<td className="max-w-xs px-3 py-2">
										<MatchTagsCell matchTags={pb.match_tags} />
									</td>
									<td className="max-w-xs px-3 py-2">
										{pb.branch_count > 0 ? (
											<div className="space-y-1">
												<div className="font-mono text-xs text-text-primary">
													{pb.branch_count} 个分支
												</div>
												<div className="flex flex-wrap gap-1">
													{pb.branch_names.slice(0, 4).map((name) => (
														<span
															key={name}
															className="rounded-full bg-surface-subtle px-1.5 py-0.5 text-xs text-text-secondary"
														>
															{name}
														</span>
													))}
													{pb.branch_names.length > 4 ? (
														<span className="text-xs text-text-muted">
															+{pb.branch_names.length - 4}
														</span>
													) : null}
												</div>
											</div>
										) : (
											<span className="text-xs text-text-muted">PB 默认</span>
										)}
									</td>
									<td className="px-3 py-2 font-mono text-xs">{pb.allowed_skills_count}</td>
									<td className="px-3 py-2 font-mono text-xs">{pb.step_budget}</td>
									<td className="px-3 py-2">
										{pb.has_pre_fetch ? (
											<span className="rounded bg-brand-success/10 px-1.5 py-0.5 text-xs text-brand-success">
												有
											</span>
										) : (
											<span className="text-xs text-text-muted">—</span>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}
